import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import NoteAddIcon from "@mui/icons-material/NoteAdd";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import SendIcon from "@mui/icons-material/Send";
import SkipNextIcon from "@mui/icons-material/SkipNext";
import SearchIcon from "@mui/icons-material/Search";
import SyncIcon from "@mui/icons-material/Sync";
import VisibilityIcon from "@mui/icons-material/Visibility";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import {
  Badge,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";

import LendingWorkQueueApi from "../../../../../infrastructure/api/lendingWorkQueue";
import LendingOperationsApi from "../../../../../infrastructure/api/lendingOperations";
import BreadcrumbsComponent from "../../../../components/shared/breadcrumbs";
import JalaliDatePicker from "../../../../components/shared/jalali-datepicker";
import EnhancedTable from "../../../../components/shared/table";
import { getCurrentUserId } from "../../../../components/request/requestAssignmentDisplay";
import {
  formatLocalDateForApi,
  toJalaliDate,
  toJalaliFullDate,
  toLocalDateInputValue,
} from "../../../../middleware/middleware";
import LendingWorkQueueCaseDrawer from "./LendingWorkQueueCaseDrawer";
import LoanDisbursementExcelImportDialog from "./LoanDisbursementExcelImportDialog";
import LoanDisbursementSingleDialog from "./LoanDisbursementSingleDialog";
import BarqPortalOtpLoginModal, {
  isBarqLoginRequired,
} from "../../shared/lending/BarqPortalOtpLoginModal";
import BarqApplicantsSubmitModal from "../../shared/lending/BarqApplicantsSubmitModal";

const OPERATIONS_SOURCE = "SolarentOperator";
const IRIA_PARTNER_CODE = "IRIA_PAY";
const BARQ_PARTNER_CODE = "BARQ_PAY";
const CHEQUE_FOLLOW_UP_ACTION = "پیگیری چک ضمانت";

export const QUEUE_TABS_BASE = [
  { key: "new", label: "درخواست جدید", countField: "newCount" },
  { key: "inProgress", label: "در جریان", countField: "inProgressCount" },
  { key: "completed", label: "تکمیل / بسته‌شده", countField: "completedCount" },
];

export const QUEUE_TABS_IRIA = [
  { key: "new", label: "درخواست جدید", countField: "newCount" },
  {
    key: "awaitingCheque",
    label: "در انتظار چک",
    countField: "awaitingChequeCount",
  },
  { key: "inProgress", label: "در جریان", countField: "inProgressCount" },
  { key: "rejected", label: "رد شده", countField: "rejectedCount" },
  { key: "paid", label: "پرداخت شده", countField: "paidCount" },
];

const emptyChequeFollowUpDialog = {
  open: false,
  row: null,
  note: "",
  nextFollowUpDate: null,
};

const parseLocalDateOnly = (value) => {
  const ymd = toLocalDateInputValue(value);
  if (!ymd) return null;
  const [y, m, d] = ymd.split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};

/** @deprecated prefer QUEUE_TABS_BASE / QUEUE_TABS_IRIA */
export const QUEUE_TABS = QUEUE_TABS_BASE;

const pick = (obj, camel, pascal) => obj?.[camel] ?? obj?.[pascal] ?? null;

const normalizeChequeFollowUpLog = (raw) => ({
  id: pick(raw, "id", "Id"),
  description: pick(raw, "description", "Description") || "",
  createdByFullName: pick(raw, "createdByFullName", "CreatedByFullName") || "",
  createOn: pick(raw, "createOn", "CreateOn"),
  nextFollowUpDate: pick(raw, "nextFollowUpDate", "NextFollowUpDate"),
  actionTitle: pick(raw, "actionTitle", "ActionTitle") || "",
});

export const normalizeQueueRow = (raw) => ({
  loanProcessId: pick(raw, "loanProcessId", "LoanProcessId"),
  requestInfoId: pick(raw, "requestInfoId", "RequestInfoId"),
  requestTrackingCode: pick(raw, "requestTrackingCode", "RequestTrackingCode"),
  applicantFullName: pick(raw, "applicantFullName", "ApplicantFullName") || "—",
  applicantMobile: pick(raw, "applicantMobile", "ApplicantMobile") || "",
  plantCapacityKw: pick(raw, "plantCapacityKw", "PlantCapacityKw"),
  lendingPartnerCode: pick(raw, "lendingPartnerCode", "LendingPartnerCode") || "",
  lendingPartnerTitle: pick(raw, "lendingPartnerTitle", "LendingPartnerTitle") || "",
  currentStatus: pick(raw, "currentStatus", "CurrentStatus"),
  currentStatusTitle: pick(raw, "currentStatusTitle", "CurrentStatusTitle") || "",
  processTypeTitle: pick(raw, "processTypeTitle", "ProcessTypeTitle") || "",
  queueTab: pick(raw, "queueTab", "QueueTab") || "new",
  currentStageTitle: pick(raw, "currentStageTitle", "CurrentStageTitle") || "",
  completedStepCount: pick(raw, "completedStepCount", "CompletedStepCount") ?? 0,
  totalStepCount: pick(raw, "totalStepCount", "TotalStepCount") ?? 0,
  referenceNumber: pick(raw, "referenceNumber", "ReferenceNumber") || "",
  trackingCode: pick(raw, "trackingCode", "TrackingCode") || "",
  partnerRejectionReason:
    pick(raw, "partnerRejectionReason", "PartnerRejectionReason") || "",
  partnerExternalStatusCode:
    pick(raw, "partnerExternalStatusCode", "PartnerExternalStatusCode") || "",
  partnerExternalStatusTitle:
    pick(raw, "partnerExternalStatusTitle", "PartnerExternalStatusTitle") || "",
  requestedAmount: pick(raw, "requestedAmount", "RequestedAmount"),
  approvedAmount: pick(raw, "approvedAmount", "ApprovedAmount"),
  assignedToUserId: pick(raw, "assignedToUserId", "AssignedToUserId"),
  assignedToFullName: pick(raw, "assignedToFullName", "AssignedToFullName") || "",
  assignedAt: pick(raw, "assignedAt", "AssignedAt"),
  startedAt: pick(raw, "startedAt", "StartedAt"),
  lastActionAt: pick(raw, "lastActionAt", "LastActionAt"),
  openDays: pick(raw, "openDays", "OpenDays") ?? 0,
  lastChequeFollowUpNote:
    pick(raw, "lastChequeFollowUpNote", "LastChequeFollowUpNote") || "",
  lastChequeFollowUpAt: pick(raw, "lastChequeFollowUpAt", "LastChequeFollowUpAt"),
  lastChequeFollowUpBy:
    pick(raw, "lastChequeFollowUpBy", "LastChequeFollowUpBy") || "",
  nextChequeFollowUpDate: pick(
    raw,
    "nextChequeFollowUpDate",
    "NextChequeFollowUpDate"
  ),
  requestRegisteredAt: pick(raw, "requestRegisteredAt", "RequestRegisteredAt"),
});

const emptyCounts = {
  newCount: 0,
  awaitingChequeCount: 0,
  inProgressCount: 0,
  completedCount: 0,
  rejectedCount: 0,
  paidCount: 0,
};

const isClosedQueueTab = (key) =>
  key === "completed" || key === "rejected" || key === "paid";

const countForTab = (counts, key) => {
  if (key === "new") return counts.newCount || 0;
  if (key === "awaitingCheque") return counts.awaitingChequeCount || 0;
  if (key === "inProgress") return counts.inProgressCount || 0;
  if (key === "rejected") return counts.rejectedCount || 0;
  if (key === "paid") return counts.paidCount || 0;
  if (key === "completed") return counts.completedCount || 0;
  return 0;
};
const LendingWorkQueuePage = ({ partnerCode, partnerTitle, subtitle }) => {
  const currentUserId = getCurrentUserId();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQuery = String(
    searchParams.get("q") || searchParams.get("search") || ""
  ).trim();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState(emptyCounts);
  const [activeTab, setActiveTab] = useState("new");
  const [experts, setExperts] = useState([]);
  const [searchText, setSearchText] = useState(initialQuery);
  const [appliedSearch, setAppliedSearch] = useState(initialQuery);
  const [assigneeFilter, setAssigneeFilter] = useState("");
  const [assignDialog, setAssignDialog] = useState({ open: false, row: null, userId: "" });
  const [chequeFollowUpDialog, setChequeFollowUpDialog] = useState(
    emptyChequeFollowUpDialog
  );
  const [chequeFollowUpSaving, setChequeFollowUpSaving] = useState(false);
  const [chequeFollowUpHistory, setChequeFollowUpHistory] = useState([]);
  const [chequeFollowUpHistoryLoading, setChequeFollowUpHistoryLoading] =
    useState(false);
  const [detailDrawer, setDetailDrawer] = useState({ open: false, row: null });
  const [busyProcessId, setBusyProcessId] = useState(null);
  const [nextStepProcessId, setNextStepProcessId] = useState(null);
  const [submitIriaProcessId, setSubmitIriaProcessId] = useState(null);
  const [syncingIriaMobile, setSyncingIriaMobile] = useState(false);
  const [syncingIriaDetails, setSyncingIriaDetails] = useState(false);
  const [syncingBarq, setSyncingBarq] = useState(false);
  const [barqLoginOpen, setBarqLoginOpen] = useState(false);
  const [pendingBarqAction, setPendingBarqAction] = useState(null); // 'sync' | 'applicants'
  const [barqApplicantsOpen, setBarqApplicantsOpen] = useState(false);
  const [loanDisbursementImportOpen, setLoanDisbursementImportOpen] =
    useState(false);
  const [loanDisbursementSingleOpen, setLoanDisbursementSingleOpen] =
    useState(false);
  const isIriaQueue = partnerCode === IRIA_PARTNER_CODE;
  const isBarqQueue = partnerCode === BARQ_PARTNER_CODE;
  const queueTabs = isIriaQueue ? QUEUE_TABS_IRIA : QUEUE_TABS_BASE;

  useEffect(() => {
    const fromUrl = String(
      searchParams.get("q") || searchParams.get("search") || ""
    ).trim();
    if (!fromUrl) return;
    setSearchText(fromUrl);
    setAppliedSearch(fromUrl);
  }, [searchParams]);

  const loadFilterData = useCallback(async () => {
    try {
      const res = await LendingWorkQueueApi.getFilterData(OPERATIONS_SOURCE, partnerCode);
      const data = res?.data ?? res;
      const list = data?.experts ?? data?.Experts ?? [];
      setExperts(Array.isArray(list) ? list : []);
    } catch {
      setExperts([]);
    }
  }, [partnerCode]);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const payload = {};
      if (appliedSearch.trim()) payload.searchText = appliedSearch.trim();
      if (assigneeFilter !== "") payload.assignedToUserId = Number(assigneeFilter);

      const res = await LendingWorkQueueApi.search(OPERATIONS_SOURCE, partnerCode, payload);
      if (res?.isSuccess === false) {
        toast.error(res?.message || "خطا در دریافت صف کار لندتک");
        setRows([]);
        setCounts({ ...emptyCounts });
        return;
      }

      const data = res?.data ?? res;
      const list = data?.rows ?? data?.Rows ?? [];
      const normalized = Array.isArray(list) ? list.map(normalizeQueueRow) : [];
      setRows(normalized);
      const nextCounts = {
        newCount: data?.newCount ?? data?.NewCount ?? 0,
        awaitingChequeCount:
          data?.awaitingChequeCount ?? data?.AwaitingChequeCount ?? 0,
        inProgressCount: data?.inProgressCount ?? data?.InProgressCount ?? 0,
        completedCount: data?.completedCount ?? data?.CompletedCount ?? 0,
        rejectedCount: data?.rejectedCount ?? data?.RejectedCount ?? 0,
        paidCount: data?.paidCount ?? data?.PaidCount ?? 0,
      };
      setCounts(nextCounts);

      // If the active tab is empty after search/filter, jump to the first tab that has rows.
      setActiveTab((prev) => {
        if (countForTab(nextCounts, prev) > 0) return prev;
        const order = isIriaQueue
          ? ["new", "awaitingCheque", "inProgress", "rejected", "paid"]
          : ["new", "inProgress", "completed"];
        const nextTab = order.find((key) => countForTab(nextCounts, key) > 0);
        return nextTab || prev;
      });
    } catch (e) {
      toast.error(e?.message || "خطا در دریافت صف کار لندتک");
      setRows([]);
      setCounts({ ...emptyCounts });
    } finally {
      setLoading(false);
    }
  }, [appliedSearch, assigneeFilter, partnerCode, isIriaQueue]);

  useEffect(() => {
    loadFilterData();
  }, [loadFilterData]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  const handleAssign = useCallback(
    async (row, assignedToUserId) => {
      if (!row?.loanProcessId) return;
      setBusyProcessId(row.loanProcessId);
      try {
        const res = await LendingWorkQueueApi.assign(
          OPERATIONS_SOURCE,
          row.loanProcessId,
          assignedToUserId || null
        );
        if (res?.isSuccess === false) {
          toast.error(res?.message || "خطا در ثبت مسئول پیگیری");
          return;
        }
        toast.success(res?.message || "مسئول پیگیری وام ثبت شد");
        await loadList();
      } catch (e) {
        toast.error(e?.message || "خطا در ثبت مسئول پیگیری");
      } finally {
        setBusyProcessId(null);
      }
    },
    [loadList]
  );

  const handleSaveChequeFollowUp = useCallback(async () => {
    const row = chequeFollowUpDialog.row;
    const note = String(chequeFollowUpDialog.note || "").trim();
    if (!row?.loanProcessId) return;
    if (!note) {
      toast.error("نتیجه پیگیری را وارد کنید.");
      return;
    }

    setChequeFollowUpSaving(true);
    try {
      const payload = {
        LoanProcessId: row.loanProcessId,
        ActionTitle: CHEQUE_FOLLOW_UP_ACTION,
        Description: note,
        Status: row.currentStatus ?? 3,
      };
      const nextDateApi = formatLocalDateForApi(
        chequeFollowUpDialog.nextFollowUpDate
      );
      if (nextDateApi) {
        payload.NextFollowUpDate = nextDateApi;
      }

      const res = await LendingOperationsApi.addLog(OPERATIONS_SOURCE, payload);
      if (res?.isSuccess === false) {
        toast.error(res?.message || "ثبت پیگیری ناموفق بود");
        return;
      }
      toast.success(res?.message || "نتیجه پیگیری چک ثبت شد");
      setChequeFollowUpDialog(emptyChequeFollowUpDialog);
      setChequeFollowUpHistory([]);
      await loadList();
    } catch (e) {
      toast.error(e?.message || "ثبت پیگیری ناموفق بود");
    } finally {
      setChequeFollowUpSaving(false);
    }
  }, [chequeFollowUpDialog, loadList]);

  const openChequeFollowUpDialog = useCallback((row) => {
    setChequeFollowUpDialog({
      open: true,
      row,
      note: "",
      nextFollowUpDate: parseLocalDateOnly(row?.nextChequeFollowUpDate),
    });
  }, []);

  useEffect(() => {
    if (!chequeFollowUpDialog.open || !chequeFollowUpDialog.row?.loanProcessId) {
      setChequeFollowUpHistory([]);
      setChequeFollowUpHistoryLoading(false);
      return undefined;
    }

    let cancelled = false;
    const loanProcessId = chequeFollowUpDialog.row.loanProcessId;

    (async () => {
      setChequeFollowUpHistoryLoading(true);
      try {
        const res = await LendingOperationsApi.getById(
          OPERATIONS_SOURCE,
          loanProcessId
        );
        if (cancelled) return;
        if (res?.isSuccess === false) {
          setChequeFollowUpHistory([]);
          return;
        }
        const data = res?.data ?? res;
        const logs = data?.logs ?? data?.Logs ?? [];
        const history = (Array.isArray(logs) ? logs : [])
          .map(normalizeChequeFollowUpLog)
          .filter((log) => log.actionTitle === CHEQUE_FOLLOW_UP_ACTION);
        setChequeFollowUpHistory(history);
      } catch {
        if (!cancelled) setChequeFollowUpHistory([]);
      } finally {
        if (!cancelled) setChequeFollowUpHistoryLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [chequeFollowUpDialog.open, chequeFollowUpDialog.row?.loanProcessId]);

  const handleSyncIriaMissingByMobile = useCallback(async () => {
    setSyncingIriaMobile(true);
    try {
      const res = await LendingOperationsApi.syncIriaMissingTrackingByMobile(
        OPERATIONS_SOURCE
      );
      if (res?.isSuccess === false) {
        toast.error(
          res?.message || "خطا در بروزرسانی پرونده‌های بدون پیگیری ایریا"
        );
        return;
      }
      const data = res?.data ?? res;
      toast.success(
        data?.message ||
          data?.Message ||
          res?.message ||
          "بروزرسانی با جستجوی موبایل انجام شد"
      );
      await loadList();
    } catch (e) {
      toast.error(
        e?.message || "خطا در بروزرسانی پرونده‌های بدون پیگیری ایریا"
      );
    } finally {
      setSyncingIriaMobile(false);
    }
  }, [loadList]);

  const handleRefreshIriaTrackedDetails = useCallback(async () => {
    setSyncingIriaDetails(true);
    try {
      const res = await LendingOperationsApi.refreshIriaTrackedLoanDetails(
        OPERATIONS_SOURCE
      );
      if (res?.isSuccess === false) {
        toast.error(
          res?.message || "خطا در بروزرسانی جزئیات پرونده‌های دارای پیگیری"
        );
        return;
      }
      const data = res?.data ?? res;
      toast.success(
        data?.message ||
          data?.Message ||
          res?.message ||
          "بروزرسانی جزئیات از صفحه detail انجام شد"
      );
      await loadList();
    } catch (e) {
      toast.error(
        e?.message || "خطا در بروزرسانی جزئیات پرونده‌های دارای پیگیری"
      );
    } finally {
      setSyncingIriaDetails(false);
    }
  }, [loadList]);

  const runBarqSync = useCallback(async () => {
    setSyncingBarq(true);
    try {
      const res = await LendingOperationsApi.syncAllBarqPortalLoans(OPERATIONS_SOURCE);
      if (isBarqLoginRequired(res)) {
        setPendingBarqAction("sync");
        setBarqLoginOpen(true);
        toast.info("برای همگام‌سازی، وارد پنل برق‌پی شوید");
        return;
      }
      if (res?.isSuccess === false) {
        toast.error(res?.message || "خطا در همگام‌سازی پرونده‌های برق‌پی");
        return;
      }
      const data = res?.data ?? res;
      const message =
        data?.message ||
        data?.Message ||
        res?.message ||
        "همگام‌سازی کامل پرونده‌های برق‌پی انجام شد";
      toast.success(message);
      await loadList();
    } catch (e) {
      toast.error(e?.message || "خطا در همگام‌سازی پرونده‌های برق‌پی");
    } finally {
      setSyncingBarq(false);
    }
  }, [loadList]);

  const handleSyncBarqStatuses = useCallback(() => {
    runBarqSync();
  }, [runBarqSync]);

  const handleBarqLoginSuccess = useCallback(async () => {
    toast.success("ورود برق‌پی ذخیره شد");
    setBarqLoginOpen(false);
    if (pendingBarqAction === "sync") {
      setPendingBarqAction(null);
      await runBarqSync();
      return;
    }
    if (pendingBarqAction === "applicants") {
      setPendingBarqAction(null);
      setBarqApplicantsOpen(true);
      return;
    }
    setPendingBarqAction(null);
  }, [pendingBarqAction, runBarqSync]);

  const hasIriaExternalId = useCallback((row) => {
    const code = String(row?.trackingCode || "").trim();
    return /^\d+$/.test(code);
  }, []);

  /** ارسال به ایریا: تب درخواست جدید وقتی شناسه عددی ایریا نیست. */
  const canShowIriaSubmit = useCallback(
    (row) => row?.queueTab === "new" && !hasIriaExternalId(row),
    [hasIriaExternalId]
  );

  /** Next فقط وقتی شناسه عددی هست و حداقل یک‌بار وضعیت از ایریا خوانده شده. */
  const canShowIriaNextStep = useCallback((row) => {
    if (!hasIriaExternalId(row)) return false;
    const status =
      row?.partnerExternalStatusCode ||
      row?.PartnerExternalStatusCode ||
      row?.partnerExternalStatusTitle ||
      row?.PartnerExternalStatusTitle ||
      "";
    return String(status).trim().length > 0;
  }, [hasIriaExternalId]);

  const handleIriaSubmit = useCallback(
    async (row) => {
      if (!row?.loanProcessId) return;
      if (!canShowIriaSubmit(row)) {
        toast.warning("ارسال فقط برای درخواست‌های جدید بدون شناسه ایریا مجاز است.");
        return;
      }

      setSubmitIriaProcessId(row.loanProcessId);
      try {
        const res = await LendingOperationsApi.submitIriaPortalApplication(
          OPERATIONS_SOURCE,
          row.loanProcessId
        );
        if (res?.isSuccess === false) {
          toast.error(res?.message || "ارسال به ایریاپی ناموفق بود");
          await loadList();
          return;
        }

        const data = res?.data ?? res?.Data ?? {};
        const message =
          data?.connectorMessage ||
          data?.ConnectorMessage ||
          res?.message ||
          "متقاضی در ایریاپی ثبت شد";
        toast.success(message);
        await loadList();
      } catch (e) {
        toast.error(e?.message || "ارسال به ایریاپی ناموفق بود");
        await loadList();
      } finally {
        setSubmitIriaProcessId(null);
      }
    },
    [canShowIriaSubmit, loadList]
  );

  const handleIriaNextStep = useCallback(
    async (row) => {
      if (!row?.loanProcessId) return;
      if (!canShowIriaNextStep(row)) {
        toast.warning(
          "این پرونده در ایریا تأیید نشده یا شناسه معتبر ندارد. ابتدا «بروزرسانی وضعیت (detail)» یا «تکمیل پیگیری با موبایل» را بزنید."
        );
        return;
      }

      setNextStepProcessId(row.loanProcessId);
      try {
        const res = await LendingOperationsApi.iriaPortalNextStep(
          OPERATIONS_SOURCE,
          row.loanProcessId
        );
        if (res?.isSuccess === false) {
          toast.error(res?.message || "اجرای مرحله بعد ایریاپی ناموفق بود");
          await loadList();
          return;
        }

        const data = res?.data ?? res?.Data ?? {};
        const message =
          data?.connectorMessage ||
          data?.ConnectorMessage ||
          res?.message ||
          "مرحله بعد در ایریاپی انجام شد";
        toast.success(message);
        await loadList();
      } catch (e) {
        toast.error(e?.message || "اجرای مرحله بعد ایریاپی ناموفق بود");
        await loadList();
      } finally {
        setNextStepProcessId(null);
      }
    },
    [canShowIriaNextStep, loadList]
  );

  const openDetails = useCallback((row) => {
    setDetailDrawer({ open: true, row });
  }, []);

  const tabRows = useMemo(
    () => rows.filter((r) => r.queueTab === activeTab),
    [rows, activeTab]
  );

  const tableRows = useMemo(
    () =>
      tabRows.map((row, index) => ({
        ...row,
        row_index: index + 1,
        capacityDisplay: row.plantCapacityKw ? `${row.plantCapacityKw} kW` : "—",
        requestRegisteredDisplay: row.requestRegisteredAt
          ? toJalaliDate(row.requestRegisteredAt)
          : "—",
        stepProgressDisplay:
          row.totalStepCount > 0
            ? `${row.completedStepCount}/${row.totalStepCount}`
            : "—",
        stageDisplay:
          isClosedQueueTab(row.queueTab)
            ? row.currentStatusTitle || "بسته‌شده"
            : row.currentStageTitle || row.currentStatusTitle || "—",
        assigneeDisplay: row.assignedToFullName || "بدون مسئول",
        assigneeColor: row.assignedToFullName
          ? Number(row.assignedToUserId) === Number(currentUserId)
            ? "success"
            : "info"
          : "default",
        lastActionDisplay: row.lastActionAt ? toJalaliFullDate(row.lastActionAt) : "—",
        openDaysDisplay:
          isClosedQueueTab(row.queueTab) ? "—" : `${row.openDays} روز`,
        iriaStatusDisplay:
          row.partnerExternalStatusTitle ||
          row.partnerExternalStatusCode ||
          "—",
        iriaMessagesPreview: row.partnerRejectionReason
          ? String(row.partnerRejectionReason).split("\n")[0]
          : "—",
        partnerStatusDisplay:
          row.partnerExternalStatusTitle ||
          row.partnerExternalStatusCode ||
          row.currentStageTitle ||
          row.currentStatusTitle ||
          "—",
        partnerDetailsPreview: row.partnerRejectionReason
          ? String(row.partnerRejectionReason).split("\n")[0]
          : row.referenceNumber ||
            row.trackingCode ||
            row.stageDisplay ||
            "—",
        chequeFollowUpDisplay: row.lastChequeFollowUpNote
          ? String(row.lastChequeFollowUpNote).slice(0, 80)
          : "—",
        chequeFollowUpAtDisplay: row.lastChequeFollowUpAt
          ? toJalaliFullDate(row.lastChequeFollowUpAt)
          : "—",
        nextChequeFollowUpDisplay: row.nextChequeFollowUpDate
          ? toJalaliDate(row.nextChequeFollowUpDate)
          : "—",
      })),
    [tabRows, currentUserId]
  );

  const headCells = useMemo(
    () => [
      { id: "requestTrackingCode", label: "کد پیگیری" },
      { id: "requestRegisteredDisplay", label: "تاریخ ثبت درخواست" },
      { id: "applicantFullName", label: "متقاضی" },
      { id: "applicantMobile", label: "موبایل" },
      { id: "capacityDisplay", label: "ظرفیت" },
      ...(isIriaQueue
        ? [
            { id: "iriaStatusDisplay", label: "وضعیت ایریا" },
            {
              id: "iriaMessagesPreview",
              label:
                activeTab === "rejected" ? "دلیل رد / پیام ایریا" : "پیام ایریا",
            },
          ]
        : isBarqQueue
        ? [
            { id: "partnerStatusDisplay", label: "وضعیت برق‌پی" },
            { id: "partnerDetailsPreview", label: "جزئیات برق‌پی" },
          ]
        : []),
      { id: "stageDisplay", label: "مرحله فعلی" },
      { id: "stepProgressDisplay", label: "پیشرفت" },
      ...(isIriaQueue && activeTab === "awaitingCheque"
        ? [
            { id: "chequeFollowUpDisplay", label: "آخرین پیگیری چک" },
            { id: "chequeFollowUpAtDisplay", label: "زمان پیگیری" },
            { id: "nextChequeFollowUpDisplay", label: "پیگیری بعدی" },
          ]
        : []),
      {
        id: "assigneeDisplay",
        label: "مسئول پیگیری",
        type: "chip",
        model: { colorField: "assigneeColor" },
      },
      { id: "lastActionDisplay", label: "آخرین اقدام" },
      { id: "openDaysDisplay", label: "روزهای باز" },
    ],
    [isIriaQueue, isBarqQueue, activeTab]
  );

  const tableActions = useMemo(
    () => [
      ...(isIriaQueue
        ? [
            {
              icon: <SendIcon fontSize="small" />,
              onClick: handleIriaSubmit,
              color: "secondary",
              tooltip: "ارسال به ایریاپی",
              hidden: (row) =>
                submitIriaProcessId === row.loanProcessId ||
                !canShowIriaSubmit(row),
            },
            {
              icon: <SkipNextIcon fontSize="small" />,
              onClick: handleIriaNextStep,
              color: "warning",
              tooltip: "مرحله بعد در ایریاپی",
              hidden: (row) =>
                isClosedQueueTab(row.queueTab) ||
                nextStepProcessId === row.loanProcessId ||
                !canShowIriaNextStep(row),
            },
          ]
        : []),
      {
        icon: <VisibilityIcon fontSize="small" />,
        onClick: openDetails,
        color: "primary",
        tooltip: "جزئیات و ثبت مرحله وام",
      },
      ...(isIriaQueue
        ? [
            {
              icon: <NoteAddIcon fontSize="small" />,
              onClick: openChequeFollowUpDialog,
              color: "secondary",
              tooltip: "ثبت نتیجه پیگیری چک",
              hidden: (row) =>
                row.queueTab !== "awaitingCheque" ||
                busyProcessId === row.loanProcessId ||
                chequeFollowUpSaving,
            },
          ]
        : []),
      {
        icon: <AssignmentIndIcon fontSize="small" />,
        onClick: (row) => handleAssign(row, null),
        color: "success",
        tooltip: "پیگیری با من",
        hidden: (row) =>
          busyProcessId === row.loanProcessId ||
          Number(row.assignedToUserId) === Number(currentUserId),
      },
      {
        icon: <PersonAddAlt1Icon fontSize="small" />,
        onClick: (row) =>
          setAssignDialog({ open: true, row, userId: row.assignedToUserId || "" }),
        color: "info",
        tooltip: "ارجاع به کارشناس",
        hidden: (row) => busyProcessId === row.loanProcessId,
      },
    ],
    [
      isIriaQueue,
      handleIriaSubmit,
      canShowIriaSubmit,
      submitIriaProcessId,
      handleIriaNextStep,
      canShowIriaNextStep,
      nextStepProcessId,
      openDetails,
      openChequeFollowUpDialog,
      handleAssign,
      busyProcessId,
      currentUserId,
      chequeFollowUpSaving,
    ]
  );

  const handleApplySearch = () => {
    const next = searchText.trim();
    setAppliedSearch(next);
    const nextParams = new URLSearchParams(searchParams);
    if (next) nextParams.set("q", next);
    else nextParams.delete("q");
    nextParams.delete("search");
    setSearchParams(nextParams, { replace: true });
  };

  const handleAssignDialogConfirm = async () => {
    const { row, userId } = assignDialog;
    setAssignDialog({ open: false, row: null, userId: "" });
    if (row && userId) await handleAssign(row, Number(userId));
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <BreadcrumbsComponent />
      </Grid>

      <Grid item xs={12}>
        <Card>
          <CardHeader
            title={`میز کار ${partnerTitle}`}
            subheader={subtitle || `پیگیری متمرکز فرآیند وام ${partnerTitle}`}
            action={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {isIriaQueue ? (
                  <>
                    <Button
                      size="small"
                      variant="outlined"
                      color="success"
                      startIcon={<UploadFileIcon />}
                      onClick={() => setLoanDisbursementSingleOpen(true)}
                      disabled={loading}
                    >
                      ثبت واریز تکی
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      color="success"
                      startIcon={<UploadFileIcon />}
                      onClick={() => setLoanDisbursementImportOpen(true)}
                      disabled={loading}
                    >
                      اکسل واریز تجمیعی
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      color="secondary"
                      startIcon={<SyncIcon />}
                      onClick={handleSyncIriaMissingByMobile}
                      disabled={
                        syncingIriaMobile ||
                        syncingIriaDetails ||
                        loading
                      }
                    >
                      {syncingIriaMobile
                        ? "جستجوی موبایل..."
                        : "تکمیل پیگیری با موبایل"}
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      color="primary"
                      startIcon={<SyncIcon />}
                      onClick={handleRefreshIriaTrackedDetails}
                      disabled={
                        syncingIriaMobile ||
                        syncingIriaDetails ||
                        loading
                      }
                    >
                      {syncingIriaDetails
                        ? "در حال خواندن detail..."
                        : "بروزرسانی وضعیت (detail)"}
                    </Button>
                  </>
                ) : null}
                {isBarqQueue ? (
                  <>
                    <Button
                      size="small"
                      variant="outlined"
                      color="success"
                      startIcon={<UploadFileIcon />}
                      onClick={() => setLoanDisbursementSingleOpen(true)}
                      disabled={loading}
                    >
                      ثبت واریز تکی
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      color="success"
                      startIcon={<UploadFileIcon />}
                      onClick={() => setLoanDisbursementImportOpen(true)}
                      disabled={loading}
                    >
                      اکسل واریز تجمیعی
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      color="secondary"
                      startIcon={<UploadFileIcon />}
                      onClick={() => setBarqApplicantsOpen(true)}
                      disabled={loading || syncingBarq}
                    >
                      ارسال اکسل به برق‌پی
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      color="primary"
                      startIcon={<SyncIcon />}
                      onClick={handleSyncBarqStatuses}
                      disabled={syncingBarq || loading}
                    >
                      {syncingBarq ? "در حال بروزرسانی همه..." : "بروزرسانی همه از برق‌پی"}
                    </Button>
                  </>
                ) : null}
                <IconButton
                  color="primary"
                  onClick={loadList}
                  aria-label="بروزرسانی"
                  disabled={
                    loading ||
                    syncingIriaMobile ||
                    syncingIriaDetails ||
                    syncingBarq
                  }
                >
                  <SyncIcon />
                </IconButton>
              </Box>
            }
          />
          <CardContent>
            <Box
              sx={{
                mb: 2,
                display: "flex",
                flexWrap: "wrap",
                gap: 1.5,
                alignItems: "center",
              }}
            >
              <TextField
                size="small"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleApplySearch()}
                placeholder="جستجو بر اساس نام، موبایل، کد پیگیری یا کد رهگیری..."
                sx={{ flex: "1 1 260px", minWidth: 220 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />

              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>مسئول پیگیری</InputLabel>
                <Select
                  label="مسئول پیگیری"
                  value={assigneeFilter}
                  onChange={(e) => setAssigneeFilter(e.target.value)}
                >
                  <MenuItem value="">همه</MenuItem>
                  {currentUserId ? (
                    <MenuItem value={currentUserId}>موارد من</MenuItem>
                  ) : null}
                  {experts
                    .filter((x) => Number(x.id ?? x.Id) !== Number(currentUserId))
                    .map((x) => (
                      <MenuItem key={x.id ?? x.Id} value={x.id ?? x.Id}>
                        {x.fullName ?? x.FullName}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>

              <Button variant="contained" onClick={handleApplySearch}>
                اعمال فیلتر
              </Button>

              <Chip size="small" variant="outlined" label={`${rows.length} فرآیند`} />
            </Box>

            <Tabs
              value={activeTab}
              onChange={(_, value) => setActiveTab(value)}
              sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}
            >
              {queueTabs.map((tab) => (
                <Tab
                  key={tab.key}
                  value={tab.key}
                  label={
                    <Badge
                      badgeContent={counts[tab.countField] || 0}
                      color={
                        tab.key === "paid" || tab.key === "completed"
                          ? "success"
                          : tab.key === "rejected"
                          ? "error"
                          : tab.key === "awaitingCheque"
                          ? "warning"
                          : "primary"
                      }
                      max={999}
                      sx={{ "& .MuiBadge-badge": { right: -14 } }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {tab.label}
                      </Typography>
                    </Badge>
                  }
                  sx={{ px: 3 }}
                />
              ))}
            </Tabs>

            <EnhancedTable
              rowKey="loanProcessId"
              rows={tableRows}
              headCells={headCells}
              actions={tableActions}
              loading={loading}
            />
          </CardContent>
        </Card>
      </Grid>

      <Dialog
        open={assignDialog.open}
        onClose={() => setAssignDialog({ open: false, row: null, userId: "" })}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>ارجاع پیگیری وام</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            متقاضی: {assignDialog.row?.applicantFullName || "—"} (کد{" "}
            {assignDialog.row?.requestTrackingCode || "—"})
          </Typography>
          <FormControl size="small" fullWidth>
            <InputLabel>کارشناس مسئول</InputLabel>
            <Select
              label="کارشناس مسئول"
              value={assignDialog.userId}
              onChange={(e) =>
                setAssignDialog((prev) => ({ ...prev, userId: e.target.value }))
              }
            >
              {experts.map((x) => (
                <MenuItem key={x.id ?? x.Id} value={x.id ?? x.Id}>
                  {x.fullName ?? x.FullName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignDialog({ open: false, row: null, userId: "" })}>
            انصراف
          </Button>
          <Button
            variant="contained"
            onClick={handleAssignDialogConfirm}
            disabled={!assignDialog.userId}
          >
            ثبت ارجاع
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={chequeFollowUpDialog.open}
        onClose={() =>
          !chequeFollowUpSaving &&
          setChequeFollowUpDialog(emptyChequeFollowUpDialog)
        }
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>ثبت نتیجه پیگیری چک ضمانت</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            متقاضی: {chequeFollowUpDialog.row?.applicantFullName || "—"} (کد{" "}
            {chequeFollowUpDialog.row?.requestTrackingCode || "—"})
          </Typography>

          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            پیگیری‌های قبلی
          </Typography>
          {chequeFollowUpHistoryLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2, mb: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : chequeFollowUpHistory.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              هنوز پیگیری ثبت نشده است.
            </Typography>
          ) : (
            <Stack
              spacing={1.25}
              divider={<Divider flexItem />}
              sx={{
                mb: 2,
                maxHeight: 220,
                overflowY: "auto",
                pr: 0.5,
                border: 1,
                borderColor: "divider",
                borderRadius: 1,
                p: 1.5,
              }}
            >
              {chequeFollowUpHistory.map((item) => (
                <Box key={item.id}>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {item.createOn ? toJalaliFullDate(item.createOn) : "—"}
                    {item.createdByFullName ? ` — ${item.createdByFullName}` : ""}
                    {item.nextFollowUpDate
                      ? ` | پیگیری بعدی: ${toJalaliDate(item.nextFollowUpDate)}`
                      : ""}
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                    {item.description || "—"}
                  </Typography>
                </Box>
              ))}
            </Stack>
          )}

          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={3}
            label="نتیجه پیگیری"
            placeholder="مثلاً: تماس با مشتری — چک هنوز آماده نشده / چک دریافت شد و در حال ارسال..."
            value={chequeFollowUpDialog.note}
            onChange={(e) =>
              setChequeFollowUpDialog((prev) => ({ ...prev, note: e.target.value }))
            }
            disabled={chequeFollowUpSaving}
            sx={{ mb: 2 }}
          />
          <JalaliDatePicker
            fullWidth
            size="small"
            label="تاریخ پیگیری بعدی (اختیاری)"
            value={chequeFollowUpDialog.nextFollowUpDate}
            onChange={(value) =>
              setChequeFollowUpDialog((prev) => ({
                ...prev,
                nextFollowUpDate: value ? parseLocalDateOnly(value) : null,
              }))
            }
            disabled={chequeFollowUpSaving}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setChequeFollowUpDialog(emptyChequeFollowUpDialog)}
            disabled={chequeFollowUpSaving}
          >
            انصراف
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleSaveChequeFollowUp}
            disabled={
              chequeFollowUpSaving || !String(chequeFollowUpDialog.note || "").trim()
            }
          >
            {chequeFollowUpSaving ? "در حال ثبت..." : "ثبت پیگیری"}
          </Button>
        </DialogActions>
      </Dialog>

      <LendingWorkQueueCaseDrawer
        open={detailDrawer.open}
        queueRow={detailDrawer.row}
        partnerTitle={partnerTitle}
        onClose={() => setDetailDrawer({ open: false, row: null })}
        onSaved={loadList}
      />

      <BarqPortalOtpLoginModal
        open={barqLoginOpen}
        onClose={() => {
          setBarqLoginOpen(false);
          setPendingBarqAction(null);
        }}
        onSuccess={handleBarqLoginSuccess}
        source={OPERATIONS_SOURCE}
      />

      <BarqApplicantsSubmitModal
        open={barqApplicantsOpen}
        onClose={() => setBarqApplicantsOpen(false)}
        source={OPERATIONS_SOURCE}
        rows={rows}
        onRequireLogin={() => {
          setBarqApplicantsOpen(false);
          setPendingBarqAction("applicants");
          setBarqLoginOpen(true);
        }}
        onDone={loadList}
      />

      <LoanDisbursementExcelImportDialog
        open={loanDisbursementImportOpen}
        source={OPERATIONS_SOURCE}
        defaultPartnerCode={partnerCode}
        onClose={() => setLoanDisbursementImportOpen(false)}
        onCommitted={loadList}
      />

      <LoanDisbursementSingleDialog
        open={loanDisbursementSingleOpen}
        source={OPERATIONS_SOURCE}
        onClose={() => setLoanDisbursementSingleOpen(false)}
        onCommitted={loadList}
      />
    </Grid>
  );
};

LendingWorkQueuePage.propTypes = {
  partnerCode: PropTypes.string.isRequired,
  partnerTitle: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
};

export default LendingWorkQueuePage;
